import { useEffect, useRef, useState } from 'react';

import { setAppVersion } from '@/monitoring/sentry.util';

const VERSION_CHECK_INTERVAL_MS = 60 * 1000;

export interface UseVersionCheckResult {
  isNewVersionAvailable: boolean;
}

export const useVersionCheck = (): UseVersionCheckResult => {
  const [isNewVersionAvailable, setIsNewVersionAvailable] = useState(false);
  const currentVersion = useRef<string | null>(null);

  useEffect(() => {
    const checkVersion = async () => {
      try {
        const response = await fetch('/version.json?t=' + Date.now());
        if (!response.ok) {
          return;
        }
        const data = (await response.json()) as { version: string };
        const serverVersion = data.version;

        if (currentVersion.current === null) {
          currentVersion.current = serverVersion;
          setAppVersion(serverVersion);
        } else if (currentVersion.current !== serverVersion) {
          setIsNewVersionAvailable(true);
        }
      } catch (error) {
        console.error('Failed to check version', error);
      }
    };

    void checkVersion();

    const checkIfVisible = () => {
      if (document.visibilityState === 'visible') {
        void checkVersion();
      }
    };

    const intervalId = setInterval(checkIfVisible, VERSION_CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', checkIfVisible);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', checkIfVisible);
    };
  }, []);

  return { isNewVersionAvailable };
};
